import React, { useState } from 'react';
import { Grid, Button, Table, Icon, Progress, Loader, Modal } from 'semantic-ui-react';

import InputReview from './InputReview';
import { runBudgetCalculator } from '~/services/budgetCalculatorService';

import './styles.scss';

// Define types for all form values
type CurrentElectricBillValues = {
    usage?: number | null;
    cost?: number | null;
};

type NewEnergySystemValues = {
    newEnergySystem?: string | null;
    costOfNewEnergySystem?: number | null;
    energySystemSize?: number | null;
    energyProductionYear1?: number | null;
    expectedElectricBillSavingsYear1?: number | null;
    expectedBillEscalationRate?: number | null;
    renewableEnergyCreditsValue?: number | null;
    operationMaintenanceCosts?: number | null;
    includeInverterReplacement?: boolean | null;
};

type AdditionalBudgetItemsValues = {
    programAdministrationOverhead?: number | null;
    otherContingency?: number | null;
};

type FederalItcValues = {
    percentage?: number | null;
};

type GrantValues = {
    id: string;
    amount?: number | null;
    availableForConstruction?: boolean;
};

type GrantsValues = {
    grants: GrantValues[];
};

type OperatingLoanValues = {
    loanName?: string | null;
    amount?: number | null;
    rate?: number | null;
    term?: number | null;
    amortization?: number | null;
    availableForConstruction?: boolean | null;
};

type LoansValues = {
    loan1: OperatingLoanValues;
    loan2: OperatingLoanValues;
};

type ConstructionLoanValues = {
    loanName?: string | null;
    amount?: number | null;
    totalInterestPayments?: number | null;
};

type BudgetCalculatorState = {
    currentElectricBill: CurrentElectricBillValues;
    newEnergySystem: NewEnergySystemValues;
    additionalBudgetItems: AdditionalBudgetItemsValues;
    federalItc: FederalItcValues;
    grants: GrantsValues;
    operatingLoans: LoansValues;
    constructionLoan: ConstructionLoanValues;
};

const BudgetCalculator: React.FC = () => {
    const [formData, setFormData] = useState<BudgetCalculatorState>({
        currentElectricBill: {},
        newEnergySystem: {},
        additionalBudgetItems: {},
        federalItc: {},
        grants: { grants: [] },
        operatingLoans: {
            loan1: {},
            loan2: {}
        },
        constructionLoan: {}
    });

    const [isCalculating, setIsCalculating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<any>(null);
    const [step, setStep] = useState<number>(0);
    const [showResetModal, setShowResetModal] = useState(false);
    const numSteps = 2;

    const handleStepChange = (direction: string) => {
        if (direction === 'next') {
            setStep(prev => Math.min(prev + 1, numSteps));
            if (step === 0) {
                window.scrollTo(0, 0);
            }
        } else if (direction === 'prev') {
            setStep(prev => Math.max(prev - 1, 1));
        }
    };

    // Map form data to backend input format
    const mapToBackendInputs = (data: BudgetCalculatorState) => {
        return [
            // Current Electric Bill inputs
            { cell: 'C6', value: data.currentElectricBill.usage || 0 }, // Annual electricity usage
            { cell: 'C7', value: data.currentElectricBill.cost || 0 }, // Annual electricity cost
            // New Energy System inputs
            { cell: 'C10', value: data.newEnergySystem.costOfNewEnergySystem || 0 }, // Cost of new energy system
            { cell: 'C11', value: data.newEnergySystem.energySystemSize || 0 }, // Energy system size
            { cell: 'C12', value: data.newEnergySystem.energyProductionYear1 || 0 }, // Energy production, year 1
            { cell: 'C13', value: data.newEnergySystem.expectedElectricBillSavingsYear1 || 0 }, // Expected electric bill escalation rate
            { cell: 'C14', value: (data.newEnergySystem.expectedBillEscalationRate || 0) / 100 }, // Expected electric bill escalation rate
            { cell: 'C15', value: data.newEnergySystem.renewableEnergyCreditsValue || 0 }, // Value of Renewable Energy Credits
            { cell: 'C16', value: data.newEnergySystem.operationMaintenanceCosts || 0 }, // Annual operation & maintenance cost
            { cell: 'C17', value: data.newEnergySystem.includeInverterReplacement ? 1 : 0 }, // Inverter replacement assumed?
            // Additional Budget Items inputs
            { cell: 'C20', value: data.additionalBudgetItems.programAdministrationOverhead || 0 }, // Program administration and overhead
            { cell: 'C21', value: data.additionalBudgetItems.otherContingency || 0 }, // Other / contingency
            // Grants
            {
                cell: 'C25',
                value:
                    data.grants.grants
                        .filter(grant => grant.availableForConstruction)
                        .reduce((sum, grant) => sum + (grant.amount || 0), 0)
            },
            {
                cell: 'C28',
                value:
                    data.grants.grants
                        .filter(grant => !grant.availableForConstruction)
                        .reduce((sum, grant) => sum + (grant.amount || 0), 0)
            },
            // Operating Loan 1
            { cell: 'C35', value: data.operatingLoans.loan1.loanName || '' }, // Loan 1 - Name
            { cell: 'C36', value: data.operatingLoans.loan1.amount || 0 }, // Loan 1 amount
            { cell: 'C37', value: (data.operatingLoans.loan1.rate || 0) / 100 }, // Loan 1 annual rate
            { cell: 'C38', value: data.operatingLoans.loan1.term || 0 }, // Loan 1 term
            { cell: 'C39', value: data.operatingLoans.loan1.amortization || 0 }, // Loan 1 amort period
            { cell: 'C40', value: data.operatingLoans.loan1.availableForConstruction ? 1 : 0 }, // Loan 1 available for construction
            // Operating Loan 2
            { cell: 'C41', value: data.operatingLoans.loan2.loanName || '' }, // Loan 2 - Name
            { cell: 'C42', value: data.operatingLoans.loan2.amount || 0 }, // Loan 2 amount
            { cell: 'C43', value: (data.operatingLoans.loan2.rate || 0) / 100 }, // Loan 2 annual rate
            { cell: 'C44', value: data.operatingLoans.loan2.term || 0 }, // Loan 2 term
            { cell: 'C45', value: data.operatingLoans.loan2.amortization || 0 }, // Loan 2 amort period
            { cell: 'C46', value: data.operatingLoans.loan2.availableForConstruction ? 1 : 0 }, // Loan 2 available for construction
            // Construction Loan
            { cell: 'C49', value: data.constructionLoan.loanName || 0 }, // Construction loan Name
            { cell: 'C50', value: data.constructionLoan.amount || 0 }, // Construction loan amount
            { cell: 'C51', value: data.constructionLoan.totalInterestPayments || 0 }, // Construction loan rate
            // Federal ITC
            { cell: 'C54', value: (data.federalItc.percentage || 0) / 100 }, // Federal investment tax credit percentage
        ];
    };

    const handleCalculate = async () => {
        setIsCalculating(true);

        try {
            const inputs = mapToBackendInputs(formData);
            const result = await runBudgetCalculator(inputs);
            setError(null);
            setIsCalculating(false);
            // Display results in a Table
            setResults({
                ...result,
                highlightsRows: result.data.highlights?.map((row: string[], idx: number) => (
                    <Table.Row key={idx}>
                        {row.map((cell: string, cellIdx: number) => (
                            <Table.Cell key={cellIdx}>{cell}</Table.Cell>
                        ))}
                    </Table.Row>
                )),
                useOfFundsRows: result.data.useOfFunds?.map((row: string[], idx: number) => (
                    <Table.Row key={idx}>
                        {row.map((cell: string, cellIdx: number) => (
                            <Table.Cell key={cellIdx}>{cell}</Table.Cell>
                        ))}
                    </Table.Row>
                )),
                sourceOfFundsRows: result.data.sourceOfFunds?.map((row: string[], idx: number) => (
                    <Table.Row key={idx}>
                        {row.map((cell: string, cellIdx: number) => (
                            <Table.Cell key={cellIdx}>{cell}</Table.Cell>
                        ))}
                    </Table.Row>
                )),
                cashRequiredRows: result.data.cashRequired?.map((row: string[], idx: number) => (
                    <Table.Row key={idx}>
                        {row.map((cell: string, cellIdx: number) => (
                            <Table.Cell key={cellIdx}>{cell}</Table.Cell>
                        ))}
                    </Table.Row>
                )),
                projectionsRows: result.data.projections?.map((row: string[], idx: number) => (
                    <Table.Row key={idx}>
                        {row.map((cell: string, cellIdx: number) => (
                            <Table.Cell key={cellIdx}>{cell}</Table.Cell>
                        ))}
                    </Table.Row>
                )),

            });
            handleStepChange('next');
        } catch (error) {
            console.error('Error running budget calculator:', error);
            setError('An error occurred while calculating the budget. Please try again.');
            setResults(null);
            setIsCalculating(false);
        }
    };

    const handleDownloadExcel = () => {
        if (results?.data?.download) {
            const { buffer, filename, mimeType } = results.data.download;

            // Convert base64 to blob
            const byteCharacters = atob(buffer);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
    };

    const handleReset = () => {
        setShowResetModal(true);
    };

    const confirmReset = () => {
        window.location.reload(); // Reload the page to reset the form
    };

    const cancelReset = () => {
        setShowResetModal(false);
    };

    const missingRequiredFields = () => {
        console.log('Checking for missing required fields at step', step, formData);
        let response = false;
        if (
            step === 1 &&
            (
                !formData.newEnergySystem.costOfNewEnergySystem ||
                !formData.newEnergySystem.energySystemSize ||
                !formData.newEnergySystem.energyProductionYear1 ||
                !formData.newEnergySystem.expectedElectricBillSavingsYear1 ||
                !formData.newEnergySystem.expectedBillEscalationRate
            )
        ) {
            response = true;
        }
        return response;
    };

    return (
        <>
            <div id="budget-calculator">
                {step !== 0 ? (
                    <div className="calculator-header">
                        <div>
                            <Icon name="x" size="large" onClick={handleReset} style={{ cursor: 'pointer', float: 'right' }} />
                            <h4>Budget Calculator</h4>
                        </div>
                        <div className="powered-by">
                            <span>Powered by</span> <img src="/conductor-logo-longform.png" alt="Conductor Logo" />
                        </div>
                    </div>
                ) : null}

                <div>
                    <Grid container centered>
                        <Grid.Column>
                            {error && <div className="error-message">{error}</div>}
                            {(() => {
                                switch (step) {
                                    case 0:
                                        return (
                                            <div className="calculator-welcome">
                                                <h1>Energy Project Budget Calculator</h1>
                                                <div className="powered-by">
                                                    <span>Powered by</span> <img src="/conductor-logo-longform.png" alt="Conductor Logo" />
                                                </div>
                                                <h2>Test different ways to pay for your energy project and understand your costs and savings.</h2>
                                                <p>Energy projects can be big investments, but they often pay for themselves over time by lowering utility bills. This free budget calculator helps you plan projects like solar, geothermal, battery storage, or other renewable energy systems and find the right mix of funding sources to make your project affordable.</p>
                                                <p>You can test and download multiple reports to compare different scenarios and choose the option that works best for your team. And if things change, like construction costs rise or a funding source falls through, you can use this calculator to try new scenarios and keep your project moving forward with confidence. You can also run scenarios for different energy technologies. Solar is a common starting point, so the tool includes default solar settings that can be easily turned off for other types of projects.</p>
                                                <p><strong>How to make the most of this tool:</strong></p>
                                                <ol>
                                                    <li>Assemble your team, including a technical lead and a financial lead</li>
                                                    <li>Work together using our prep workbook to collect project information</li>
                                                    <li>Enter your data into the calculator and run financing scenarios</li>
                                                </ol>
                                                <p><strong>Each scenario generates a report summarizing:</strong></p>
                                                <ul>
                                                    <li>Cash needed for construction and your net cash out of pocket</li>
                                                    <li>Payback period</li>
                                                    <li>30-year cash flow projection</li>
                                                </ul>
                                                <p><strong>Downloads</strong></p>
                                                <a href="/Conductor%20Solar%20Budget%20Calculator%20-%20Workbook.xlsx" download>
                                                    <Icon name="file" />
                                                    Prep workbook (xlsx)
                                                </a>
                                                <br />
                                                <a href="/Conductor%20Solar%20Budget%20Calculator%20-%20Example%20Output.xlsx" download>
                                                    <Icon name="file" />
                                                    Example output (xlsx)
                                                </a>

                                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                        <Button onClick={() => handleStepChange('next')}>
                                                            <Icon name="arrow circle right" />
                                                            Start Now
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    case 1:
                                        return (
                                            <div className="calculator-summary">
                                                {isCalculating ? (
                                                    <div className="loading-container">
                                                        <h2>Calculating...</h2>
                                                        <p>Please wait while we run your calculations.</p>
                                                        <Loader active inline="centered" size="large"></Loader>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <h1>Enter your information</h1>
                                                        <p>Make sure all information is correct before calculating your budget.</p>

                                                        <p style={{ fontStyle: 'italic', marginTop: '-12px', marginBottom: '24px' }}>Required inputs are marked with a red asterisk.<label className="required"></label></p>

                                                        <InputReview
                                                            values={formData}
                                                            onChange={(values) => setFormData(values)}
                                                            onCalculate={handleCalculate}
                                                        />
                                                    </>
                                                )}

                                            </div>
                                        );
                                    case 2:
                                        return (
                                            <div className="results-container">
                                                <div className="results-header">
                                                    <h1>Calculation Results</h1>
                                                </div>
                                                <h3>Economic summary</h3>
                                                <Table basic compact="very">
                                                    <Table.Body>
                                                        {results.highlightsRows}
                                                    </Table.Body>
                                                </Table>

                                                <h3>Sources of funds</h3>
                                                <Table className="total-row sources-of-funds" basic compact="very">
                                                    <Table.Header>
                                                        <Table.Row>
                                                            <Table.HeaderCell>Name</Table.HeaderCell>
                                                            <Table.HeaderCell></Table.HeaderCell>
                                                            <Table.HeaderCell>Construction</Table.HeaderCell>
                                                            <Table.HeaderCell>Initial Operation</Table.HeaderCell>
                                                        </Table.Row>
                                                    </Table.Header>
                                                    <Table.Body>
                                                        {results.sourceOfFundsRows}
                                                    </Table.Body>
                                                </Table>

                                                <h3>Uses of funds</h3>
                                                <Table className="total-row uses-of-funds" basic compact="very">
                                                    <Table.Header>
                                                        <Table.Row>
                                                            <Table.HeaderCell></Table.HeaderCell>
                                                            <Table.HeaderCell></Table.HeaderCell>
                                                            <Table.HeaderCell>Construction</Table.HeaderCell>
                                                            <Table.HeaderCell>Initial Operation</Table.HeaderCell>
                                                        </Table.Row>
                                                    </Table.Header>
                                                    <Table.Body>
                                                        {results.useOfFundsRows}
                                                    </Table.Body>
                                                </Table>
                                                <h3>Projections</h3>
                                                <div style={{ overflowX: 'auto' }}>
                                                    <Table className="projections" basic compact="very" unstackable>
                                                        <Table.Header>
                                                            <Table.Row>
                                                                <Table.HeaderCell rowSpan="2" style={{ borderRight: '1px solid #ddd' }}>Year</Table.HeaderCell>
                                                                <Table.HeaderCell colSpan="2" textAlign="center">Energy System</Table.HeaderCell>
                                                                <Table.HeaderCell colSpan="2" textAlign="center">Financing</Table.HeaderCell>
                                                                <Table.HeaderCell colSpan="2" textAlign="center">Cash flow</Table.HeaderCell>
                                                            </Table.Row>
                                                            <Table.Row>
                                                                <Table.HeaderCell>Electric bill savings & REC income</Table.HeaderCell>
                                                                <Table.HeaderCell>System Costs</Table.HeaderCell>
                                                                <Table.HeaderCell>Grants & financing distributions</Table.HeaderCell>
                                                                <Table.HeaderCell>Financing payments</Table.HeaderCell>
                                                                <Table.HeaderCell>Annual cash flow</Table.HeaderCell>
                                                                <Table.HeaderCell>Cumulative cash flow</Table.HeaderCell>
                                                            </Table.Row>
                                                        </Table.Header>
                                                        <Table.Body>
                                                            {results.projectionsRows}
                                                        </Table.Body>
                                                    </Table>
                                                </div>
                                            </div>
                                        );
                                    default:
                                        return null;
                                }
                            })()}

                        </Grid.Column>
                    </Grid>

                </div>


                {
                    step !== 0 ? (
                        <div className="calculator-footer">
                            <Button
                                outlined
                                size='large'
                                onClick={() => handleStepChange('prev')}
                                icon="arrow left"
                                disabled={isCalculating || step <= 1}
                                style={{
                                    visibility: step > 1 ? 'visible' : 'hidden',
                                    pointerEvents: step > 1 ? 'auto' : 'none'
                                }}
                            >
                                Back
                            </Button>
                            {step < numSteps - 1 ? (
                                <Button
                                    size="large"
                                    onClick={() => handleStepChange('next')}
                                    icon="arrow right"
                                    disabled={missingRequiredFields()}
                                >
                                    Next
                                </Button>
                            ) : null}
                            {step === numSteps - 1 ? (
                                <Button
                                    size="large"
                                    onClick={handleCalculate}
                                    loading={isCalculating}
                                    disabled={isCalculating || missingRequiredFields()}
                                >
                                    <Icon name="calculator" />
                                    Calculate
                                </Button>
                            ) : null}

                            {step === numSteps ? (
                                <div className="results-actions">
                                    <Button
                                        size="large"
                                        icon="download"
                                        secondary
                                        content="Download Excel Report"
                                        onClick={handleDownloadExcel}
                                        disabled={!results?.data?.download}
                                    />
                                    <Button
                                        size="large"
                                        onClick={handleReset}
                                        style={{ marginLeft: '1rem' }}
                                    >
                                        Finish
                                    </Button>
                                </div>

                            ) : null}

                            {/* <Button
                                    secondary
                                    size="large"
                                    onClick={autopopulateForm}
                                    disabled={isCalculating}
                                >
                                    Auto-Populate Example Data
                                </Button> */}
                        </div>
                    ) : null
                }

                {/* Reset Confirmation Modal */}
                <Modal
                    open={showResetModal}
                    onClose={cancelReset}
                    size="small"
                >
                    <Modal.Header>Exit Calculator</Modal.Header>
                    <Modal.Content>
                        <p><strong>Are you sure you want to exit the calculator? All data will be lost.</strong></p>
                        {results?.data?.download && (
                            <>
                                <p>To save your results, download the Excel report before exiting.</p>
                                <Button
                                    icon="download"
                                    size="small"
                                    content="Download Excel Report"
                                    onClick={handleDownloadExcel}
                                    color="blue"
                                />
                            </>
                        )}
                    </Modal.Content>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px' }}>
                        <Button secondary onClick={cancelReset}>
                            Cancel
                        </Button>
                        <Button onClick={confirmReset} color="red">
                            Exit Calculator
                        </Button>

                    </div>
                </Modal>

            </div >
        </>
    );
};

export default BudgetCalculator;